import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { ChevronRight, Mic2, Power } from 'lucide-react';
import { usePersonas } from '@/hooks/useVoiceApi';
import { useStopVoice, useVoiceStatus } from '@/hooks/useVoiceApi';
import { InactivityTimeoutControl } from '@/components/InactivityTimeoutControl';
import { UserMenu } from '@/components/UserMenu';
import type { Persona } from '@/types';

const LANG_META: Record<string, { flag: string; name: string }> = {
  de: { flag: '🇩🇪', name: 'Deutsch' },
  en: { flag: '🇬🇧', name: 'English' },
  fr: { flag: '🇫🇷', name: 'Français' },
  es: { flag: '🇪🇸', name: 'Español' },
  it: { flag: '🇮🇹', name: 'Italiano' },
  pt: { flag: '🇵🇹', name: 'Português' },
  nl: { flag: '🇳🇱', name: 'Nederlands' },
  pl: { flag: '🇵🇱', name: 'Polski' },
  ru: { flag: '🇷🇺', name: 'Русский' },
  zh: { flag: '🇨🇳', name: '中文' },
  ja: { flag: '🇯🇵', name: '日本語' },
  ko: { flag: '🇰🇷', name: '한국어' },
};
function langLabel(code: string) {
  const m = LANG_META[code.toLowerCase()];
  return m ? `${m.flag} ${m.name}` : code.toUpperCase();
}

function PersonaCard({ persona, onSelect }: { persona: Persona; onSelect: () => void }) {
  return (
    <button
      onClick={onSelect}
      className="flex w-full items-center gap-4 rounded-xl border border-white/10 bg-white/5 p-4 text-left transition hover:border-white/25 hover:bg-white/10 active:scale-[0.99]"
    >
      <div className="flex h-12 w-12 shrink-0 items-center justify-center overflow-hidden rounded-full bg-white/10 text-lg font-semibold text-white">
        {persona.imageUrl ? (
          <img src={persona.imageUrl} alt={persona.name} className="h-full w-full object-cover" />
        ) : (
          persona.name.charAt(0).toUpperCase()
        )}
      </div>
      <div className="min-w-0 flex-1">
        <p className="font-medium text-white">{persona.name}</p>
        <p className="mt-0.5 text-xs text-white/60">Digital Twin · {langLabel(persona.language)}</p>
      </div>
      <ChevronRight className="h-4 w-4 shrink-0 text-white/30" />
    </button>
  );
}

function servicesReady(status: { asr: string; tts: string; livekit: string } | undefined): boolean {
  if (!status) return false;
  return status.asr === 'InService' && status.tts === 'InService' && status.livekit === 'running';
}

export function PersonaSelectPage() {
  const navigate = useNavigate();
  const { data: personas, isLoading } = usePersonas();
  const { data: status, isSuccess: statusLoaded } = useVoiceStatus();
  const stopVoice = useStopVoice();

  const [showConfirm, setShowConfirm] = useState(false);

  useEffect(() => {
    if (statusLoaded && !servicesReady(status)) {
      navigate('/', { replace: true });
    }
  }, [statusLoaded, status, navigate]);

  const enabled = personas?.filter((p) => p.enabled && (p.enrollmentStatus === undefined || p.enrollmentStatus === 'ready')) ?? [];

  async function handleShutdown() {
    await stopVoice.mutateAsync();
    navigate('/');
  }

  return (
    <div className="flex min-h-screen flex-col bg-black">
      {/* Header */}
      <header className="flex items-center justify-between border-b border-white/10 px-4 py-3">
        <div className="flex items-center gap-2">
          <Mic2 className="h-5 w-5 text-white" />
          <span className="font-medium text-white">Twin Agent Voice</span>
        </div>
        <div className="flex items-center gap-3">
          {/* Status dots */}
          <div className="flex items-center gap-1.5">
            <span
              className={`h-1.5 w-1.5 rounded-full ${status?.asr === 'InService' ? 'bg-green-400' : 'bg-white/20'}`}
              title="ASR"
            />
            <span
              className={`h-1.5 w-1.5 rounded-full ${status?.tts === 'InService' ? 'bg-green-400' : 'bg-white/20'}`}
              title="TTS"
            />
          </div>
          <InactivityTimeoutControl compact />
          <button
            onClick={() => setShowConfirm(true)}
            disabled={stopVoice.isPending}
            className="flex h-8 w-8 items-center justify-center rounded-full border border-white/10 text-white/50 transition hover:border-red-500/50 hover:text-red-400 disabled:opacity-40"
            aria-label="Shutdown"
          >
            {stopVoice.isPending
              ? <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-current border-t-transparent" />
              : <Power className="h-4 w-4" />
            }
          </button>
          <UserMenu />
        </div>
      </header>

      {/* Content */}
      <main className="flex flex-1 flex-col items-center px-4 py-10">
        <div className="w-full max-w-sm">
          <h2 className="mb-1 text-xl font-semibold text-white">Choose a persona</h2>
          <p className="mb-6 text-sm text-white/40">Select who you'd like to interview</p>

          {isLoading ? (
            <div className="flex justify-center py-12">
              <div className="h-6 w-6 animate-spin rounded-full border-2 border-white border-t-transparent" />
            </div>
          ) : enabled.length === 0 ? (
            <p className="text-center text-sm text-white/30">No personas available</p>
          ) : (
            <div className="space-y-2">
              {enabled.map((p) => (
                <PersonaCard
                  key={p.id}
                  persona={p}
                  onSelect={() => navigate(`/call/${p.id}`)}
                />
              ))}
            </div>
          )}
        </div>
      </main>

      {/* Shutdown confirmation dialog */}
      {showConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 px-6 backdrop-blur-sm">
          <div className="w-full max-w-xs rounded-2xl border border-white/10 bg-zinc-900 p-6 shadow-2xl">
            <div className="mb-4 flex h-10 w-10 items-center justify-center rounded-full bg-red-500/15">
              <Power className="h-5 w-5 text-red-400" />
            </div>
            <h3 className="mb-1 text-base font-semibold text-white">Shutdown services?</h3>
            <p className="mb-6 text-sm text-white/50">
              This will stop the voice agent and all background services. You'll need to restart before starting a new call.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowConfirm(false)}
                className="flex-1 rounded-xl border border-white/10 py-2.5 text-sm text-white/70 transition hover:bg-white/5"
              >
                Cancel
              </button>
              <button
                onClick={async () => { setShowConfirm(false); await handleShutdown(); }}
                className="flex-1 rounded-xl bg-red-600 py-2.5 text-sm font-medium text-white transition hover:bg-red-700"
              >
                Shutdown
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
